import type { Drafts, HydrostaticRow } from '../types';

const MISTRAL_API_URL = 'https://api.mistral.ai/v1/chat/completions';
const API_KEY = import.meta.env.VITE_MISTRAL_API_KEY;

export interface OCRResult {
  text: string;
  structured?: {
    drafts?: Partial<Drafts>;
    hydrostatic?: HydrostaticRow[];
    vesselName?: string;
    lbp?: number;
  };
}

export async function ocrImage(files: File | File[], promptType: 'drafts' | 'hydrostatic' | 'vessel' = 'drafts'): Promise<OCRResult> {
  if (!API_KEY) {
    throw new Error('Mistral API key not configured. Set VITE_MISTRAL_API_KEY environment variable.');
  }

  const fileArray = Array.isArray(files) ? files : [files];
  const base64 = await fileToBase64(fileArray[0]);
  const dataUrl = `data:${fileArray[0].type};base64,${base64}`;

  const prompts: Record<string, string> = {
    drafts: `Extract draft survey readings from this image. Return ONLY a JSON object with this exact structure:
{
  "fwd_port": number,
  "fwd_stbd": number,
  "mid_port": number,
  "mid_stbd": number,
  "aft_port": number,
  "aft_stbd": number,
  "density": number
}
If a value is not visible, use 0. All values in meters. Density in t/m³.`,

    hydrostatic: `Extract hydrostatic table data from this image. Return ONLY a JSON array of objects with this exact structure:
[
  {"draft": number, "displacement": number, "tpc": number, "mtc": number, "lcf": number}
]
Each row represents one draft value. All numbers only.`,

    vessel: `Extract vessel information from this image. Return ONLY a JSON object:
{
  "name": string,
  "lbp": number,
  "imo": string (optional)
}
LBP in meters.`
  };

  const response = await fetch(MISTRAL_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${API_KEY}`,
    },
    body: JSON.stringify({
      model: 'pixtral-12b-2409',
      messages: [
        {
          role: 'user',
          content: [
            { type: 'text', text: prompts[promptType] },
            { type: 'image_url', image_url: { url: dataUrl } }
          ]
        }
      ],
      max_tokens: 2048,
      temperature: 0.1,
    }),
  });

  if (!response.ok) {
    const err = await response.text();
    throw new Error(`Mistral API error: ${response.status} - ${err}`);
  }

  const data = await response.json();
  const text = data.choices?.[0]?.message?.content || '';

  let structured;
  try {
    const jsonMatch = text.match(/```json\n?([\s\S]*?)\n?```/) || text.match(/\{[\s\S]*\}/) || text.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      structured = JSON.parse(jsonMatch[1] || jsonMatch[0]);
    }
  } catch {
    // JSON not found, return raw text only
  }

  return { text, structured };
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function parseOCRDrafts(structured: any): Partial<Drafts> {
  if (!structured) return {};
  return {
    fwd_port: Number(structured.fwd_port) || 0,
    fwd_stbd: Number(structured.fwd_stbd) || 0,
    mid_port: Number(structured.mid_port) || 0,
    mid_stbd: Number(structured.mid_stbd) || 0,
    aft_port: Number(structured.aft_port) || 0,
    aft_stbd: Number(structured.aft_stbd) || 0,
    density: Number(structured.density) || 1.025,
  };
}

export function parseOCRHydrostatic(structured: any): HydrostaticRow[] {
  if (!Array.isArray(structured)) return [];
  return structured
    .filter((r: any) => r && typeof r.draft === 'number')
    .map((r: any) => ({
      draft: Number(r.draft) || 0,
      displacement: Number(r.displacement) || 0,
      tpc: Number(r.tpc) || 0,
      mtc: Number(r.mtc) || 0,
      lcf: Number(r.lcf) || 0,
    }));
}
